import { Helmet } from './components/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import HeroBanner from './components/HeroBanner';
import FeaturedComics from './components/FeaturedComics';
import PopularComics from './components/PopularComics';
import LatestUpdates from './components/LatestUpdates';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const homeSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '韩漫网',
  url: 'https://hanmanwang.com',
  description: '韩漫网整合韩漫大全、下拉式韩国漫画和在线观看功能，提供一站式韩漫阅读解决方案。',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://hanmanwang.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '韩漫网',
  url: 'https://hanmanwang.com',
  description: '韩漫大全 - 下拉式韩国漫画在线观看平台',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '400-888-6666',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
};

export default function Home() {
  return (
    <>
      <Helmet
        title="韩漫网 - 韩漫大全 | 下拉式韩国漫画在线观看平台"
        description="韩漫网整合韩漫大全、下拉式韩国漫画和在线观看功能，提供一站式韩漫阅读解决方案。海量韩国漫画免费在线看，连载更新快，画质高清，让您的阅读过程无需跳转，体验连贯流畅。"
        keywords="韩漫网,韩漫大全,下拉式韩国漫画,韩漫在线观看,韩漫官网站,韩国漫画,免费漫画"
        canonicalUrl="https://hanmanwang.com"
        schema={[orgSchema, homeSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <HeroBanner />

          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-primary-100 text-foreground-700 hover:text-primary-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </span>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <FeaturedComics />
          <PopularComics />
          <LatestUpdates />

          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-[1280px] mx-auto text-center">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">
                为什么选择<strong>韩漫网</strong>？
              </h2>
              <p className="text-sm text-foreground-500 max-w-2xl mx-auto mb-10">
                我们致力于为您打造最佳的韩漫阅读体验
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: 'ri-stack-line', title: '海量资源', desc: '超过一万部韩国漫画，涵盖恋爱、奇幻、动作等多种类型' },
                  { icon: 'ri-scroll-to-bottom-line', title: '下拉式阅读', desc: '流畅的纵向滚动阅读体验，无需翻页，一气呵成' },
                  { icon: 'ri-speed-up-line', title: '极速更新', desc: '同步韩国最新连载，每日更新，追更不等待' },
                  { icon: 'ri-device-line', title: '多端适配', desc: '电脑、平板、手机全平台支持，随时随地畅享韩漫' },
                ].map((item, i) => (
                  <div key={i} className="p-6 rounded-lg bg-background-50 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                      <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                        <i className={`${item.icon} text-xl`}></i>
                      </span>
                    </div>
                    <h3 className="font-heading text-base text-foreground-900 mb-2">{item.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 px-4 md:px-6">
            <div className="max-w-[1280px] mx-auto">
              <div className="bg-primary-500 rounded-2xl p-8 md:p-14 text-center relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-background-50"></div>
                  <div className="absolute -bottom-10 -left-10 w-60 h-60 rounded-full bg-background-50"></div>
                </div>
                <div className="relative z-10">
                  <h2 className="font-heading text-2xl md:text-3xl text-background-50 mb-3">
                    开启您的韩漫之旅
                  </h2>
                  <p className="text-background-100 text-sm md:text-base max-w-xl mx-auto mb-6">
                    上万部精选韩国漫画等您来探索，立即加入韩漫网，享受极致的下拉式阅读体验！
                  </p>
                  <Link
                    to="/category/all"
                    className="inline-flex items-center gap-2 px-8 py-3 bg-background-50 hover:bg-background-100 text-primary-600 font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    免费开始阅读
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}